/**
 * What a MULTI-FILE pick is allowed to do, and what happens to each file in it (ui#664).
 *
 * Plain TypeScript — this module imports no React (epic #26) — because both rules it holds are the kind
 * a picker would otherwise re-derive in a hook: how many of the chosen files fit, and what a batch means
 * when one of them fails. Filling a six-slot treatment used to take six round trips through the file
 * dialog; letting one dialog fill it is the whole feature, and the arithmetic behind it is small enough
 * that hiding it inside a component would be the only reason it could ever be wrong.
 *
 * Generic in the picked element and the produced value: the pickers pass `File`s and get back uploaded
 * assets, and the tests pass strings. Nothing here reads a field of either.
 */

/** What survived the capacity check, and how much of the pick did not. */
export interface BatchAdmission<T> {
	/** The picks that fit, in PICK ORDER — which is the order they will be uploaded and shown. */
	admitted: readonly T[];
	/** How many picks were dropped for want of a slot. Zero whenever the whole pick fitted. */
	refused: number;
}

/**
 * Takes the leading `capacity - alreadyChosen` picks and counts the rest as refused.
 *
 * From the FRONT rather than by asking the sender to choose again: a dialog that admits nothing because
 * seven files were selected for six slots is the round-trip this feature exists to remove. The caller
 * says how many were dropped so the surface can say so too — silently ignoring picks would leave a
 * sender believing images are queued that never were.
 *
 * `alreadyChosen` may legitimately EXCEED `capacity` — on the sponsor page, picking six creatives and
 * then moving the treatment to a one-slot one does exactly that — so the free-slot count is floored at
 * zero. Without that floor a negative slice takes files off the END of the pick, admitting images while
 * the set is already over its limit.
 */
export function admitPickedFiles<T>(
	picked: readonly T[],
	alreadyChosen: number,
	capacity: number,
): BatchAdmission<T> {
	const free = Math.max(0, capacity - alreadyChosen);
	const admitted = picked.slice(0, free);
	return {admitted, refused: picked.length - admitted.length};
}

/** A file the batch could not process, kept beside the error so the surface can name both. */
export interface BatchFailure<T> {
	picked: T;
	error: unknown;
}

/** Everything a finished batch produced: the values that worked, and the files that did not. */
export interface BatchReport<T, R> {
	/** Produced values, in pick order — the order of the files that SUCCEEDED, gaps closed. */
	succeeded: readonly R[];
	/** The files that failed, in pick order. Empty when every file went through. */
	failures: readonly BatchFailure<T>[];
}

/**
 * Runs `step` over every picked file, one at a time, and reports each outcome separately.
 *
 * ONE FILE AT A TIME, deliberately. Each step compresses a picture in a worker that holds a decoded
 * raster and a WebAssembly codec, so running a six-file pick concurrently multiplies that peak by six on
 * exactly the phones whose browsers already kill such a worker for memory — the failure
 * `compressPickedImage`'s watchdog exists for. It also keeps the uploads in pick order on the wire, which
 * is what makes the resulting positions the sender's own ordering rather than a race.
 *
 * NEVER REJECTS. A batch that threw on its first bad file would throw away work already done — the four
 * pictures that uploaded fine before the corrupt fifth — and force the sender to pick all of them again.
 * Every error is caught and returned, so the caller keeps the successes and can name each failure. A
 * `step` that throws synchronously is caught too: it is the same defect to the sender, and letting that
 * one escape would make the guarantee depend on where in `step` the fault happened to be.
 */
export async function processPickedBatch<T, R>(
	picked: readonly T[],
	step: (item: T) => Promise<R>,
): Promise<BatchReport<T, R>> {
	const succeeded: R[] = [];
	const failures: BatchFailure<T>[] = [];
	for (const item of picked) {
		try {
			succeeded.push(await step(item));
		} catch (error) {
			failures.push({picked: item, error});
		}
	}
	return {succeeded, failures};
}
